import axios from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchJwt } from '../../utils'
import { ChatMessage } from './types'

const STRAPI_BASE = `${process.env.REACT_APP_API_HOST}/api` || ''
const LOCAL_STORAGE_KEY = 'jamiipesa-ai-conversations'
const MAX_LOCAL_CONVERSATIONS = 20

export interface Conversation {
  id: string
  title: string
  messages: ChatMessage[]
  updatedAt: string
}

interface UseConversationsReturn {
  conversations: Conversation[]
  activeConversation: Conversation | null
  isLoadingConversations: boolean
  createConversation: () => Conversation
  selectConversation: (id: string) => void
  deleteConversation: (id: string) => void
  addMessage: (message: ChatMessage) => void
  updateTitle: (id: string, title: string) => void
}

// ─── localStorage helpers ───
function loadLocalConversations(): Conversation[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveLocalConversations(conversations: Conversation[]): void {
  localStorage.setItem(
    LOCAL_STORAGE_KEY,
    JSON.stringify(conversations.slice(0, MAX_LOCAL_CONVERSATIONS))
  )
}

// ─── Strapi helpers ───
function authHeaders() {
  const token = fetchJwt()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function fetchStrapiConversations(): Promise<Conversation[]> {
  try {
    const { data } = await axios.get(`${STRAPI_BASE}/conversations`, {
      headers: authHeaders(),
      params: {
        sort: 'updatedAt:desc',
        'populate[chatMessages][sort]': 'createdAt:asc',
        'pagination[pageSize]': MAX_LOCAL_CONVERSATIONS
      }
    })
    return (data.data || []).map((item: any) => ({
      id: String(item.documentId || item.id),
      title: item.title || 'New Conversation',
      updatedAt: item.updatedAt,
      messages: (item.chatMessages || []).map((msg: any) => ({
        id: String(msg.documentId || msg.id),
        role: msg.role,
        content: msg.content,
        timestamp: msg.createdAt,
        tickers: msg.tickers,
        companies: msg.companies,
        summary: msg.summary
      }))
    }))
  } catch {
    return []
  }
}

async function createStrapiConversation(title: string): Promise<string | null> {
  try {
    const { data } = await axios.post(
      `${STRAPI_BASE}/conversations`,
      { data: { title } },
      { headers: authHeaders() }
    )
    return String(data.data.documentId || data.data.id)
  } catch {
    return null
  }
}

async function deleteStrapiConversation(id: string): Promise<void> {
  try {
    await axios.delete(`${STRAPI_BASE}/conversations/${id}`, {
      headers: authHeaders()
    })
  } catch {
    // swallow
  }
}

async function createStrapiMessage(conversationId: string, message: ChatMessage): Promise<void> {
  try {
    await axios.post(
      `${STRAPI_BASE}/chat-messages`,
      {
        data: {
          role: message.role,
          content: message.content,
          tickers: message.tickers || [],
          companies: message.companies || [],
          summary: message.summary || '',
          webSearchPerformed: false,
          conversation: conversationId
        }
      },
      { headers: authHeaders() }
    )
  } catch {
    // swallow — message is still in local state
  }
}

async function updateStrapiConversationTitle(id: string, title: string): Promise<void> {
  try {
    await axios.put(
      `${STRAPI_BASE}/conversations/${id}`,
      { data: { title } },
      { headers: authHeaders() }
    )
  } catch {
    // swallow
  }
}

// ─── Hook ───
export const useConversations = (isAuthenticated: boolean): UseConversationsReturn => {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const activeIdRef = useRef<string | null>(null)
  const [isLoadingConversations, setIsLoadingConversations] = useState(false)

  // Keep ref in sync so addMessage always reads the latest activeId
  useEffect(() => {
    activeIdRef.current = activeId
  }, [activeId])

  const activeConversation = conversations.find((c) => c.id === activeId) || null

  // Load conversations on mount and when auth changes
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoadingConversations(true)
      if (isAuthenticated) {
        const convos = await fetchStrapiConversations()
        if (!cancelled) {
          setConversations(convos)
          if (convos.length > 0 && !activeId) setActiveId(convos[0].id)
        }
      } else {
        const convos = loadLocalConversations()
        if (!cancelled) {
          setConversations(convos)
          if (convos.length > 0 && !activeId) setActiveId(convos[0].id)
        }
      }
      if (!cancelled) setIsLoadingConversations(false)
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  // Persist local conversations whenever they change (anon only)
  useEffect(() => {
    if (!isAuthenticated) {
      saveLocalConversations(conversations)
    }
  }, [conversations, isAuthenticated])

  const createConversation = useCallback((): Conversation => {
    const newConvo: Conversation = {
      id: `local-${Date.now()}`,
      title: 'New Conversation',
      messages: [],
      updatedAt: new Date().toISOString()
    }

    if (isAuthenticated) {
      // Create in Strapi async, update ID when done
      createStrapiConversation(newConvo.title).then((strapiId) => {
        if (strapiId) {
          setConversations((prev) =>
            prev.map((c) => (c.id === newConvo.id ? { ...c, id: strapiId } : c))
          )
          setActiveId((prevId) => (prevId === newConvo.id ? strapiId : prevId))
        }
      })
    }

    setConversations((prev) => [newConvo, ...prev])
    setActiveId(newConvo.id)
    return newConvo
  }, [isAuthenticated])

  const selectConversation = useCallback((id: string) => {
    setActiveId(id)
  }, [])

  const deleteConversation = useCallback(
    (id: string) => {
      setConversations((prev) => prev.filter((c) => c.id !== id))
      if (activeId === id) setActiveId(null)
      if (isAuthenticated) {
        deleteStrapiConversation(id)
      }
    },
    [activeId, isAuthenticated]
  )

  const addMessage = useCallback(
    (message: ChatMessage) => {
      setConversations((prev) => {
        let targetId = activeIdRef.current

        // Auto-create conversation if none active
        if (!targetId) {
          const newConvo: Conversation = {
            id: `local-${Date.now()}`,
            title: message.role === 'user' ? message.content.slice(0, 60) : 'New Conversation',
            messages: [message],
            updatedAt: new Date().toISOString()
          }
          targetId = newConvo.id
          setActiveId(newConvo.id)

          if (isAuthenticated) {
            createStrapiConversation(newConvo.title).then((strapiId) => {
              if (strapiId) {
                setConversations((p) =>
                  p.map((c) => (c.id === newConvo.id ? { ...c, id: strapiId } : c))
                )
                setActiveId((prevId) => (prevId === newConvo.id ? strapiId : prevId))
                createStrapiMessage(strapiId, message)
              }
            })
          }
          return [newConvo, ...prev]
        }

        // Auto-title on first user message
        const updated = prev.map((c) => {
          if (c.id !== targetId) return c
          const shouldTitle =
            c.title === 'New Conversation' && message.role === 'user' && c.messages.length === 0
          const newTitle = shouldTitle ? message.content.slice(0, 60) : c.title

          if (shouldTitle && isAuthenticated) {
            updateStrapiConversationTitle(c.id, newTitle)
          }

          return {
            ...c,
            title: newTitle,
            messages: [...c.messages, message],
            updatedAt: new Date().toISOString()
          }
        })

        if (isAuthenticated && targetId) {
          createStrapiMessage(targetId, message)
        }

        return updated
      })
    },
    [isAuthenticated]
  )

  const updateTitle = useCallback(
    (id: string, title: string) => {
      setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)))
      if (isAuthenticated) {
        updateStrapiConversationTitle(id, title)
      }
    },
    [isAuthenticated]
  )

  return {
    conversations,
    activeConversation,
    isLoadingConversations,
    createConversation,
    selectConversation,
    deleteConversation,
    addMessage,
    updateTitle
  }
}
